import { useEffect, useState } from "react";
import { authApi } from "../lib/api";

// Number formatting helpers
const fmt2 = new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtNum = (n) => fmt2.format(Number(n || 0));
const fmtMoney = (n) => `$${fmt2.format(Number(n || 0))}`;

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  async function loadLeads() {
    setLoading(true);
    try {
      const { data } = await authApi.get("/leads");
      setLeads(data);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || "Failed to load leads";
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadLeads(); }, []);

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;

  return (
  <div className="container">
    <div className="header-bar">
      <h2>Leads</h2>
      <button className="btn-accent btn-sm" onClick={loadLeads}>Refresh</button>
    </div>

      {/* Mobile card list */}
      <div className="show-sm lead-grid">
        {leads.map(l => (
          <div className="panel lead-card" key={l._id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <strong>{l.fullName || '—'}</strong>
              <small className="muted">{new Date(l.createdAt).toLocaleString()}</small>
            </div>
            <div className="hr" />
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Email</span>
              <span>{l.email}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Phone</span>
              <span>{l.phone}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Company</span>
              <span>{l.company || '—'}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Origin</span>
              <span>{l.origin}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Destination</span>
              <span>{l.destination}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Feet</span>
              <span>{fmtNum(l.feet)}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Pounds</span>
              <span>{fmtNum(l.pounds)}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Budget</span>
              <span>{l.budget != null ? fmtMoney(l.budget) : '—'}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Notes</span>
              <span style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.notes || '—'}</span>
            </div>
            <div className="hr" />
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Standard</span>
              <span>{l.prices?.standard != null ? fmtMoney(l.prices.standard) : '—'}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Time Sensitive</span>
              <span>{l.prices?.timeSensitive != null ? fmtMoney(l.prices.timeSensitive) : '—'}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Expedited</span>
              <span>{l.prices?.expedited != null ? fmtMoney(l.prices.expedited) : '—'}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Specialty</span>
              <span>{l.prices?.specialty != null ? fmtMoney(l.prices.specialty) : '—'}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Preload Next Day</span>
              <span>{l.prices?.preloadNextDay != null ? fmtMoney(l.prices.preloadNextDay) : '—'}</span>
            </div>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">Selected</span>
              <span>{Array.isArray(l.selected) && l.selected.length > 0 ? l.selected.join(", ") : "—"}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hide-sm">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Full Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Company</th>
                <th>Origin</th>
                <th>Destination</th>
                <th>Feet</th>
                <th>Pounds</th>
                <th>Standard</th>
                <th>Time Sensitive</th>
                <th>Expedited</th>
                <th>Specialty</th>
                <th>Preload/Garaging</th>
                <th>Selected</th>
                <th>Budget</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(l => (
                <tr key={l._id}>
                  <td>{new Date(l.createdAt).toLocaleString()}</td>
                  <td>{l.fullName}</td>
                  <td>{l.email}</td>
                  <td>{l.phone}</td>
                  <td>{l.company || "—"}</td>
                  <td>{l.origin}</td>
                  <td>{l.destination}</td>
                  <td>{fmtNum(l.feet)}</td>
                  <td>{fmtNum(l.pounds)}</td>
                  <td>{l.prices?.standard != null ? fmtMoney(l.prices.standard) : "—"}</td>
                  <td>{l.prices?.timeSensitive != null ? fmtMoney(l.prices.timeSensitive) : "—"}</td>
                  <td>{l.prices?.expedited != null ? fmtMoney(l.prices.expedited) : "—"}</td>
                  <td>{l.prices?.specialty != null ? fmtMoney(l.prices.specialty) : "—"}</td>
                  <td>{l.prices?.preloadNextDay != null ? fmtMoney(l.prices.preloadNextDay) : "—"}</td>
                  <td>{Array.isArray(l.selected) && l.selected.length > 0 ? l.selected.join(", ") : "—"}</td>
                  <td>{l.budget != null ? fmtMoney(l.budget) : "—"}</td>
                  <td style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* later: filters, pagination, export CSV, auth, etc. */}
    </div>
  );
}